import React, { useMemo, useState } from 'react';

import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import CountryFlag from '../../components/common/CountryFlag';
import AdminLayout from '../../components/layout/AdminLayout';
import MobilePageBar from '../../components/layout/MobilePageBar';
import BucketListTable from '../../components/member/BucketListTable';
import CoursesPlayedRanking from '../../components/member/CoursesPlayedRanking';
import HomeCoursesTable from '../../components/member/HomeCoursesTable';
import {
  GOLF_TAB_IDS,
  GolfTabId,
  PROFILE_ROUTE,
} from '../../constants/GolferProfileConstants';
import { useMyBucketList } from '../../hooks/useMemberBucketListApi';
import { useMyCoursesPlayed } from '../../hooks/useMemberCoursesPlayedApi';
import { useMyHomeCourses } from '../../hooks/useMemberHomeCourseApi';
import { useMyProfile } from '../../hooks/useMemberProfileApi';
import { MemberCoursesPlayedSummaryDTO } from '../../services/apiClient';

// "1 course", "12 courses".
const plural = (count: number, one: string, many: string): string =>
  `${count.toLocaleString()} ${count === 1 ? one : many}`;

interface Group {
  // Lower-cased, per CLAUDE.md: GUID casing is not guaranteed to survive a round trip.
  id: string;
  name: string;
  countryName: string | null;
  courses: MemberCoursesPlayedSummaryDTO[];
}

// Courses played, grouped by country or by state. A course with no country (or state) set is left
// out of that grouping, the same rule the profile's counts use (MemberCoursesPlayedService), so the
// tab's number always matches the number on the profile.
const groupBy = (
  played: MemberCoursesPlayedSummaryDTO[],
  by: 'country' | 'state',
): Group[] => {
  const groups = new Map<string, Group>();
  played.forEach((row) => {
    const id = by === 'country' ? row.countryId : row.stateId;
    const name = by === 'country' ? row.countryName : row.stateName;
    if (id == null || name == null) {
      return;
    }
    const key = id.toLowerCase();
    const group = groups.get(key) ?? {
      id: key,
      name,
      countryName: row.countryName ?? null,
      courses: [],
    };
    group.courses.push(row);
    groups.set(key, group);
  });
  // Most played first: the country a member has played most is the one they look for.
  return Array.from(groups.values()).sort(
    (a, b) =>
      b.courses.length - a.courses.length || a.name.localeCompare(b.name),
  );
};

const matches = (text: string | null | undefined, query: string): boolean =>
  query.length === 0 || (text ?? '').toLowerCase().includes(query);

const rowClass =
  'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50 dark:hover:bg-white/5 dark:focus-visible:bg-white/5';

// A country or a state, with how many courses the member has played there.
const GroupRow: React.FC<{
  group: Group;
  showCountry: boolean;
  onOpen: () => void;
}> = ({ group, showCountry, onOpen }) => (
  <li>
    <button type="button" onClick={onOpen} className={rowClass}>
      <CountryFlag name={group.countryName} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-ink dark:text-white">
          {group.name}
        </p>
        {showCountry && group.countryName && (
          <p className="truncate text-xs text-ink-muted dark:text-white/50">
            {group.countryName}
          </p>
        )}
      </div>
      <span className="text-sm text-ink-muted dark:text-white/60">
        {plural(group.courses.length, 'course', 'courses')}
      </span>
      <ChevronRight className="h-5 w-5 shrink-0 text-ink-muted dark:text-white/40" />
    </button>
  </li>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="px-4 py-10 text-center text-sm text-ink-muted dark:text-white/60">
    {children}
  </p>
);

// The member's golf, Instagram style: the profile's counters (courses, countries, bucket list)
// all open this one page, each on its own tab, the way followers and following share a page.
// The tabs are not filters. They are the same golf seen in different groups, and a country or a
// state opens to the courses played there. The address carries the tab and the open group
// (?tab=countries&group=<id>), so the phone's back button closes a group before it leaves.
const ProfileGolf: React.FC = () => {
  const { data: profile } = useMyProfile();
  const { data: played, isLoading: isPlayedLoading } = useMyCoursesPlayed();
  const { data: bucketList, isLoading: isBucketLoading } = useMyBucketList();
  const { data: homeCourses, isLoading: isHomeLoading } = useMyHomeCourses();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const requested = searchParams.get('tab');
  const tab: GolfTabId = GOLF_TAB_IDS.includes(requested as GolfTabId)
    ? (requested as GolfTabId)
    : 'courses';
  const openGroupId = searchParams.get('group')?.toLowerCase() ?? null;
  const [search, setSearch] = useState('');
  const query = search.trim().toLowerCase();

  const coursesPlayed = useMemo(() => played ?? [], [played]);
  const countries = useMemo(
    () => groupBy(coursesPlayed, 'country'),
    [coursesPlayed],
  );
  const states = useMemo(
    () => groupBy(coursesPlayed, 'state'),
    [coursesPlayed],
  );
  const bucket = bucketList ?? [];
  const home = homeCourses ?? [];

  const tabs: { id: GolfTabId; count: number; label: [string, string] }[] = [
    {
      id: 'courses',
      count: coursesPlayed.length,
      label: ['course', 'courses'],
    },
    {
      id: 'countries',
      count: countries.length,
      label: ['country', 'countries'],
    },
    { id: 'states', count: states.length, label: ['state', 'states'] },
    {
      id: 'bucket',
      count: bucket.length,
      label: ['bucket list', 'bucket list'],
    },
    { id: 'home', count: home.length, label: ['home course', 'home courses'] },
  ];

  // A tab swap replaces the address (like swiping tabs), opening a group adds to it (so back
  // closes the group first).
  const selectTab = (id: GolfTabId): void => {
    setSearch('');
    setSearchParams({ tab: id }, { replace: true });
  };
  const openGroup = (id: string): void => {
    setSearch('');
    setSearchParams({ tab, group: id });
  };

  // Back out of an open country or state. Opened from the list, that is one step back in history,
  // so the phone's back button and this agree. Landed on directly (a shared link, a reload with
  // nothing before it), there is nothing to go back to, so it swaps to the list instead.
  const closeGroup = (): void => {
    if (location.key !== 'default') {
      navigate(-1);
    } else {
      setSearchParams({ tab }, { replace: true });
    }
  };

  const groups =
    tab === 'countries' ? countries : tab === 'states' ? states : [];
  const activeGroup =
    openGroupId != null
      ? groups.find((group) => group.id === openGroupId)
      : undefined;

  const isLoading =
    tab === 'bucket'
      ? isBucketLoading
      : tab === 'home'
        ? isHomeLoading
        : isPlayedLoading;

  const renderBody = (): React.ReactNode => {
    if (isLoading) {
      return <Empty>Loading...</Empty>;
    }

    if (tab === 'courses') {
      // The full ranking: drag to rank, the Played switch, add, share. The same list as the
      // desktop Courses Played page, which a phone never reaches.
      return <CoursesPlayedRanking search={query} inset />;
    }

    if (tab === 'countries' || tab === 'states') {
      if (activeGroup != null) {
        return (
          <>
            <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3 dark:border-white/10">
              <button
                type="button"
                onClick={closeGroup}
                aria-label={
                  tab === 'countries' ? 'All countries' : 'All states'
                }
                className="-ml-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-ink-muted hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:text-white/60 dark:hover:bg-white/10"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <CountryFlag name={activeGroup.countryName} />
              <p className="flex-1 font-display text-base font-semibold text-ink dark:text-white">
                {activeGroup.name}
              </p>
              <span className="text-sm text-ink-muted dark:text-white/60">
                {plural(activeGroup.courses.length, 'course', 'courses')}
              </span>
            </div>
            {/* The same ranked table as the Courses tab, narrowed to this place. */}
            <CoursesPlayedRanking
              search={query}
              inset
              place={
                tab === 'countries'
                  ? { countryId: activeGroup.id, name: activeGroup.name }
                  : { stateId: activeGroup.id, name: activeGroup.name }
              }
            />
          </>
        );
      }
      const rows = groups.filter(
        (group) =>
          matches(group.name, query) || matches(group.countryName, query),
      );
      if (groups.length === 0) {
        return (
          <Empty>
            {tab === 'countries'
              ? 'No countries yet. They fill in as you add courses played.'
              : 'No states yet. They fill in as you add courses played.'}
          </Empty>
        );
      }
      return rows.length === 0 ? (
        <Empty>Nothing matches that.</Empty>
      ) : (
        <ul>
          {rows.map((group) => (
            <GroupRow
              key={group.id}
              group={group}
              showCountry={tab === 'states'}
              onOpen={() => openGroup(group.id)}
            />
          ))}
        </ul>
      );
    }

    if (tab === 'bucket') {
      // The same table as the desktop Bucket List page.
      return <BucketListTable search={query} inset />;
    }

    // The same table as the desktop Home Courses page: add, remove, show or hide.
    return <HomeCoursesTable search={query} inset />;
  };

  const name =
    profile != null
      ? `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim()
      : 'My golf';

  return (
    <AdminLayout
      activeMenuItem={PROFILE_ROUTE}
      breadcrumbs={[
        { label: 'My Profile', href: PROFILE_ROUTE },
        { label: 'My golf', href: '#' },
      ]}
      allowedRoles={['User']}
      mobileBar={
        <MobilePageBar
          title={name || 'My golf'}
          // Inside a country or state, back closes it first, then a second back leaves.
          backTo={PROFILE_ROUTE}
          // Inside a country or state, back closes it first, then a second back leaves.
          onBack={activeGroup != null ? closeGroup : undefined}
          backLabel="Back to my profile"
        />
      }
    >
      <div className="lg:m-4 lg:max-w-3xl lg:overflow-hidden lg:rounded-lg lg:border lg:border-gray-200 lg:bg-white lg:shadow-sm dark:lg:border-white/10 dark:lg:bg-white/5">
        {/* The same tab look as Instagram's followers page: the count is the label. It scrolls
            sideways on a phone, so all five fit without squeezing. */}
        <div
          role="tablist"
          aria-label="My golf"
          className="flex overflow-x-auto border-b border-gray-200 bg-white dark:border-white/10 dark:bg-secondary lg:dark:bg-transparent"
        >
          {tabs.map((item) => {
            const isActive = item.id === tab;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => selectTab(item.id)}
                className={`shrink-0 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold transition-colors focus:outline-none focus-visible:bg-primary/10 ${
                  isActive
                    ? 'border-primary text-ink dark:text-white'
                    : 'border-transparent text-ink-muted hover:text-ink dark:text-white/50 dark:hover:text-white'
                }`}
              >
                {plural(item.count, item.label[0], item.label[1])}
              </button>
            );
          })}
        </div>

        <div className="bg-white dark:bg-secondary lg:dark:bg-transparent">
          <div className="px-4 pt-3 pb-1">
            <label className="relative block">
              <span className="sr-only">Search</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted dark:text-white/40" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search"
                className="w-full rounded-lg border-0 bg-gray-100 py-2 pl-9 pr-3 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-primary dark:bg-white/10 dark:text-white dark:placeholder:text-white/40"
              />
            </label>
          </div>
          <div role="tabpanel" className="pb-4">
            {renderBody()}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ProfileGolf;
